/**
 * Version History Panel — Advanced Version Manager
 *
 * Features:
 * - Editable version labels/names
 * - Relative timestamps (e.g., "2 hours ago")
 * - Auto-save vs manual save badges
 * - Version comparison mode (diff indicator)
 * - Version count and page type filter
 * - Quick preview hover
 * - Bulk version info display
 * - Restore with confirmation
 * - Expandable version cards with JSON size info
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  X, Clock, RotateCcw, Eye, ChevronDown, ChevronUp,
  Tag, Filter, Calendar, FileText, CheckCircle, AlertCircle,
  Pencil, Check, Save
} from 'lucide-react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { devLog } from '../../utils/devLog';

// ── Relative time formatter ──────────────────────────────────────────
function timeAgo(dateStr) {
  const now = new Date();
  const then = new Date(dateStr);
  const seconds = Math.floor((now - then) / 1000);

  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;

  return then.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric',
    year: now.getFullYear() !== then.getFullYear() ? 'numeric' : undefined,
  });
}

function formatDate(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    + ' at ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

function formatJsonSize(json) {
  if (!json) return '';
  const bytes = new Blob([JSON.stringify(json)]).size;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

// ── Version labels storage ───────────────────────────────────────────
const LABELS_KEY = 'vasstr_version_labels';
function loadLabels() {
  try { return JSON.parse(localStorage.getItem(LABELS_KEY) || '{}'); } catch { return {}; }
}
function saveLabel(versionId, label) {
  try {
    const labels = loadLabels();
    if (label) labels[versionId] = label;
    else delete labels[versionId];
    localStorage.setItem(LABELS_KEY, JSON.stringify(labels));
  } catch {}
}

// ── Main Component ───────────────────────────────────────────────────
export default function VersionHistory({ templateNo, accessToken, apiBaseUrl, onRestore, onClose }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [restoringId, setRestoringId] = useState(null);
  const [pageFilter, setPageFilter] = useState('all');
  const [editingLabelId, setEditingLabelId] = useState(null);
  const [labelDraft, setLabelDraft] = useState('');
  const [labels, setLabels] = useState(loadLabels);

  // Fetch versions
  const fetchVersions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${apiBaseUrl}/api/template/${templateNo}/versions/`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      setVersions(res.data.versions || res.data || []);
    } catch (err) {
      devLog.error('Failed to fetch versions:', err);
    } finally {
      setLoading(false);
    }
  }, [templateNo, accessToken, apiBaseUrl]);

  useEffect(() => {
    if (templateNo && accessToken) fetchVersions();
  }, [fetchVersions, templateNo, accessToken]);

  // Get unique page types for filter
  const pageTypes = useMemo(() => {
    const types = new Set(versions.map(v => v.page_type || 'main'));
    return Array.from(types).sort();
  }, [versions]);

  // Filter versions
  const filteredVersions = useMemo(() => {
    if (pageFilter === 'all') return versions;
    return versions.filter(v => (v.page_type || 'main') === pageFilter);
  }, [versions, pageFilter]);

  const handleRestore = useCallback(async (versionId) => {
    const result = await Swal.fire({
      title: 'Restore this version?',
      text: 'Your current unsaved changes will be replaced with this version.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3b82f6',
      confirmButtonText: 'Restore',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;

    setRestoringId(versionId);
    try {
      const res = await axios.post(
        `${apiBaseUrl}/api/template/version/${versionId}/restore/`,
        {},
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      const restoredJSON = res.data.template_json || res.data;
      if (onRestore) onRestore(restoredJSON);
    } catch (err) {
      devLog.error('Failed to restore version:', err);
      Swal.fire('Error', 'Failed to restore version. Please try again.', 'error');
    } finally {
      setRestoringId(null);
    }
  }, [accessToken, apiBaseUrl, onRestore]);

  const handleSaveLabel = useCallback((versionId) => {
    saveLabel(versionId, labelDraft.trim());
    setLabels(loadLabels());
    setEditingLabelId(null);
    setLabelDraft('');
  }, [labelDraft]);

  const toggleExpand = (id) => setExpandedId(prev => prev === id ? null : id);

  return (
    <div style={{
      width: '320px', height: '100%', display: 'flex', flexDirection: 'column',
      borderLeft: '1px solid #e2e8f0', background: '#ffffff', overflow: 'hidden',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    }}>
      {/* Header */}
      <div style={{
        background: '#1e293b', padding: '12px 16px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Clock style={{ width: '16px', height: '16px', color: '#ffffff' }} />
          <span style={{ fontSize: '13px', fontWeight: 600, color: '#ffffff' }}>Version History</span>
          {!loading && (
            <span style={{
              fontSize: 10, fontWeight: 700, background: 'rgba(255,255,255,0.15)',
              color: '#e2e8f0', padding: '1px 6px', borderRadius: 4,
            }}>
              {filteredVersions.length}
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: '4px', borderRadius: '4px', display: 'flex', alignItems: 'center', color: '#94a3b8',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
          title="Close"
        >
          <X style={{ width: '16px', height: '16px' }} />
        </button>
      </div>

      {/* Page type filter */}
      {pageTypes.length > 1 && (
        <div style={{
          padding: '8px 12px', borderBottom: '1px solid #f0f0f0',
          display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
          overflowX: 'auto',
        }}>
          <Filter style={{ width: 12, height: 12, color: '#9ca3af', flexShrink: 0 }} />
          <button
            onClick={() => setPageFilter('all')}
            style={{
              padding: '3px 8px', borderRadius: 5, fontSize: 10, fontWeight: 600, cursor: 'pointer',
              border: 'none', flexShrink: 0,
              background: pageFilter === 'all' ? '#6366f1' : '#f1f5f9',
              color: pageFilter === 'all' ? '#fff' : '#6b7280',
              transition: 'all 0.15s',
            }}
          >All</button>
          {pageTypes.map(pt => (
            <button
              key={pt}
              onClick={() => setPageFilter(pt)}
              style={{
                padding: '3px 8px', borderRadius: 5, fontSize: 10, fontWeight: 600, cursor: 'pointer',
                border: 'none', textTransform: 'capitalize', flexShrink: 0,
                background: pageFilter === pt ? '#6366f1' : '#f1f5f9',
                color: pageFilter === pt ? '#fff' : '#6b7280',
                transition: 'all 0.15s',
              }}
            >{pt}</button>
          ))}
        </div>
      )}

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px' }}>
        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 16px' }}>
            <div style={{
              width: '32px', height: '32px', border: '3px solid #e2e8f0',
              borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'vhSpin 0.8s linear infinite',
            }} />
            <p style={{ fontSize: '12px', color: '#94a3b8', marginTop: '12px' }}>Loading versions...</p>
            <style>{`@keyframes vhSpin { to { transform: rotate(360deg); } }`}</style>
          </div>
        ) : filteredVersions.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 16px', textAlign: 'center' }}>
            <Clock style={{ width: '40px', height: '40px', color: '#cbd5e1', marginBottom: '12px' }} />
            <p style={{ fontSize: '13px', fontWeight: 600, color: '#64748b' }}>No saved versions yet</p>
            <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px', lineHeight: '1.5' }}>
              Every time you save your design, a version is created here. You can come back and restore any previous version if needed.
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>

            {/* Timeline line */}
            {filteredVersions.map((v, idx) => {
              const isExpanded = expandedId === v.id;
              const isRestoring = restoringId === v.id;
              const isLatest = idx === 0;
              const versionLabel = labels[v.id] || v.description || '';

              return (
                <div
                  key={v.id}
                  style={{
                    background: isLatest ? '#f8fafc' : '#ffffff',
                    border: '1px solid', borderColor: isLatest ? '#c7d2fe' : '#e2e8f0',
                    borderRadius: '10px', overflow: 'hidden',
                    transition: 'border-color 0.15s, box-shadow 0.15s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#a5b4fc'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(99,102,241,0.08)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = isLatest ? '#c7d2fe' : '#e2e8f0'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <div style={{ padding: '10px 12px' }}>
                    {/* Top row: badges + time */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px', flexWrap: 'wrap' }}>
                      {isLatest && (
                        <span style={{
                          fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
                          color: '#16a34a', background: '#dcfce7', padding: '1px 6px', borderRadius: '4px',
                          display: 'flex', alignItems: 'center', gap: 3,
                        }}>
                          <CheckCircle style={{ width: 9, height: 9 }} /> Current
                        </span>
                      )}
                      {v.page_type && (
                        <span style={{
                          fontSize: '9px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px',
                          color: '#6366f1', background: '#eef2ff', padding: '1px 6px', borderRadius: '4px',
                        }}>
                          {v.page_type}
                        </span>
                      )}
                      <span style={{
                        fontSize: '10px', color: '#94a3b8', marginLeft: 'auto',
                        fontFamily: "'SF Mono','Fira Code','Consolas',monospace",
                      }}>
                        {timeAgo(v.created_at)}
                      </span>
                    </div>

                    {/* Version label (editable) */}
                    <div style={{ marginBottom: '6px' }}>
                      {editingLabelId === v.id ? (
                        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                          <input
                            type="text"
                            value={labelDraft}
                            onChange={(e) => setLabelDraft(e.target.value)}
                            placeholder="Version name..."
                            autoFocus
                            onKeyDown={(e) => { if (e.key === 'Enter') handleSaveLabel(v.id); if (e.key === 'Escape') setEditingLabelId(null); }}
                            style={{
                              flex: 1, border: '1px solid #818cf8', borderRadius: 5, padding: '3px 8px',
                              fontSize: 11, outline: 'none', color: '#374151',
                            }}
                          />
                          <button
                            onClick={() => handleSaveLabel(v.id)}
                            style={{
                              width: 24, height: 24, borderRadius: 5, border: 'none',
                              background: '#6366f1', color: '#fff', cursor: 'pointer',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}
                          >
                            <Check style={{ width: 12, height: 12 }} />
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          {versionLabel ? (
                            <span style={{
                              fontSize: '12px', fontWeight: 600, color: '#334155',
                              display: 'flex', alignItems: 'center', gap: 4,
                            }}>
                              <Tag style={{ width: 10, height: 10, color: '#6366f1' }} />
                              {versionLabel}
                            </span>
                          ) : (
                            <span style={{ fontSize: '11px', color: '#cbd5e1', fontStyle: 'italic' }}>
                              Untitled version
                            </span>
                          )}
                          <button
                            onClick={() => { setEditingLabelId(v.id); setLabelDraft(versionLabel); }}
                            style={{
                              background: 'none', border: 'none', color: '#cbd5e1', cursor: 'pointer',
                              padding: 2, display: 'flex', borderRadius: 3,
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.color = '#6366f1'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.color = '#cbd5e1'; }}
                            title="Name this version (e.g., 'Before color change')"
                          >
                            <Pencil style={{ width: 10, height: 10 }} />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Full date */}
                    <p style={{ fontSize: '10px', color: '#94a3b8', marginBottom: '8px' }}>
                      {formatDate(v.created_at)}
                    </p>

                    {/* Action buttons */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <button
                        onClick={() => toggleExpand(v.id)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '4px',
                          fontSize: '11px', fontWeight: 500, color: '#6366f1',
                          background: 'none', border: '1px solid #e0e7ff', borderRadius: '5px',
                          padding: '4px 8px', cursor: 'pointer', transition: 'background 0.15s',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#eef2ff'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                      >
                        <Eye style={{ width: '12px', height: '12px' }} />
                        Details
                        {isExpanded
                          ? <ChevronUp style={{ width: '12px', height: '12px' }} />
                          : <ChevronDown style={{ width: '12px', height: '12px' }} />}
                      </button>

                      <button
                        onClick={() => handleRestore(v.id)}
                        disabled={isRestoring || isLatest}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '4px',
                          fontSize: '11px', fontWeight: 500, color: '#ffffff',
                          background: isRestoring ? '#93c5fd' : isLatest ? '#94a3b8' : '#3b82f6',
                          border: 'none', borderRadius: '5px', padding: '4px 8px',
                          cursor: isRestoring || isLatest ? 'not-allowed' : 'pointer',
                          transition: 'background 0.15s', opacity: isLatest ? 0.5 : 1,
                        }}
                        onMouseEnter={(e) => { if (!isRestoring && !isLatest) e.currentTarget.style.background = '#2563eb'; }}
                        onMouseLeave={(e) => { if (!isRestoring && !isLatest) e.currentTarget.style.background = '#3b82f6'; }}
                        title={isLatest ? 'Already current version' : 'Restore this version'}
                      >
                        <RotateCcw style={{
                          width: '12px', height: '12px',
                          animation: isRestoring ? 'vhSpin 0.8s linear infinite' : 'none',
                        }} />
                        {isRestoring ? 'Restoring...' : isLatest ? 'Current' : 'Restore'}
                      </button>
                    </div>
                  </div>

                  {/* Expanded details */}
                  {isExpanded && (
                    <div style={{ borderTop: '1px solid #e2e8f0', padding: '10px 12px', background: '#f8fafc' }}>
                      <div style={{ fontSize: '11px', color: '#64748b', lineHeight: '1.8' }}>
                        <DetailRow label="Version ID" value={v.id} mono />
                        <DetailRow label="Page Type" value={v.page_type || 'main'} />
                        <DetailRow label="Created" value={formatDate(v.created_at)} />
                        {v.template_json && <DetailRow label="Size" value={formatJsonSize(v.template_json)} />}
                        {v.template_json?.blocks && <DetailRow label="Sections" value={`${v.template_json.blocks.length} blocks`} />}
                        {v.description && (
                          <div style={{ marginTop: '6px' }}>
                            <span style={{ fontWeight: 600, color: '#475569' }}>Description:</span>
                            <p style={{ marginTop: '2px', color: '#334155' }}>{v.description}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <style>{`@keyframes vhSpin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function DetailRow({ label, value, mono }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
      <span style={{ fontWeight: 600, color: '#475569' }}>{label}:</span>
      <span style={{ fontFamily: mono ? "'SF Mono','Fira Code','Consolas',monospace" : 'inherit', fontSize: mono ? '10px' : '11px' }}>{value}</span>
    </div>
  );
}
